import { environment } from '../../../environments/environment';
import { versionInfo } from '../../version';
import { AnalyticsWindow } from './types';

declare let window: AnalyticsWindow;

/**
 * Returns the physical screen resolution and pixel density of the user's display device.
 * These values reflect the hardware display and do not change when the browser is resized.
 */
const getScreenProperties = () => ({
  screen: {
    width: window.screen.width,
    height: window.screen.height,
    density: window.devicePixelRatio,
  },
});

/**
 * Returns the current viewport dimensions — the visible area of the browser window.
 * Unlike screen dimensions, these values change when the browser is resized or when
 * toolbars, sidebars, or on-screen keyboards appear.
 */
const getViewportProperties = () => ({
  viewport: {
    width: window.innerWidth,
    height: window.innerHeight,
  },
});

export const analytics = {
  /**
   * Track user events with custom event data.
   *
   * @param eventName - Name of the event to track
   * @param eventData - Additional properties to include with the event
   */
  track: (eventName: string, eventData?: Record<string, unknown>) => {
    void window.analytics.track(eventName, {
      ...eventData,
      env: environment.appEnv,
      app_version: versionInfo.version,
    }, { ...getScreenProperties(), ...getViewportProperties() });
  },

  /**
   * Track page views with category and page name.
   *
   * @param category - Category of the page (e.g., 'Survey', 'Dashboard')
   * @param pageName - Name of the page
   * @param pageData - Additional properties to include with the page view
   */
  page: (category: string, pageName: string, pageData?: Record<string, unknown>) => {
    void window.analytics.page(category, pageName, {
      ...pageData,
      env: environment.appEnv,
      app_version: versionInfo.version,
    }, { ...getScreenProperties(), ...getViewportProperties() });
  },

  /**
   * Identify a user with custom user data.
   *
   * @param userId - Unique identifier for the user
   * @param userData - Additional properties to include with the user identification
   */
  identify: (userId: string, userData?: Record<string, unknown>) => {
    void window.analytics.identify(userId, {
      ...userData,
      env: environment.appEnv,
      app_version: versionInfo.version,
    }, { ...getScreenProperties(), ...getViewportProperties() });
  },

  /**
   * Associate a user with a group or organization.
   *
   * @param groupId - Unique identifier for the group
   * @param groupData - Additional properties to include with the group
   */
  group: (groupId: string, groupData?: Record<string, unknown>) => {
    void window.analytics.group(groupId, groupData);
  },
};
